/**
 * PermissionsContext.tsx
 *
 * Provides the current user's permission set to the whole app — the thin
 * frontend consumption layer over migration 0057's roles-as-data foundation
 * (user_roles ⋈ role_permissions, gated by the SELECT RLS added in 0057).
 *
 * Design (peer to ModuleContext, deliberately NOT merged with it):
 *  1. On the current user changing, loads the permission_keys granted to that
 *     user in the default workspace.
 *  2. Exposes `hasPermission(key)` — the one-line gate for NEW features.
 *  3. DEFAULT-DENY-WHILE-LOADING: the set starts empty and `hasPermission`
 *     returns false until the fetch lands. This is the intentional divergence
 *     from ModuleContext.isEnabled (which default-ALLOWs). Callers can read
 *     `loading` to render a spinner instead of a flash-of-denied.
 *  4. super_admin's seeded set is the single '*' sentinel; `hasPermission`
 *     mirrors the DB helper by treating '*' as allow-all (see permissionUtils).
 *
 * Load path — TWO-QUERY fallback (not a nested embed):
 *   A direct `user_roles -> role_permissions(...)` PostgREST embed does NOT
 *   resolve: those tables share no direct FK, only a transitive one through
 *   `roles` (both carry role_id -> roles.id). Rather than rely on a
 *   through-roles embed, we issue two simple queries (role_ids, then the
 *   permission_keys for those role_ids). Both ride the 0057 workspace-scoped
 *   SELECT RLS, so the client only ever sees its own workspace's rows.
 *
 * The workspace comes from the signed-in person's profile (`useWorkspaceId`), which is the
 * same row the database's own `current_user_workspace()` reads — so the app and the security
 * rules can never disagree about which company's roles are being asked for.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth, useWorkspaceId } from './AuthContext';
import { permissionGranted } from './permissionUtils';

// ─── Context type ─────────────────────────────────────────────────────────────

interface PermissionsContextValue {
  /** True while the initial (or a refresh) permission load is in progress. */
  loading: boolean;
  /** The current user's granted permission keys (may contain the '*' sentinel). */
  permissions: string[];
  /**
   * True if the current user is granted `key`. Mirrors the DB helper:
   *   has('*') (super_admin allow-all) OR has(key).
   * Returns false while loading and for logged-out users (default-deny).
   */
  hasPermission: (key: string) => boolean;
  /** Force a reload of the current user's permissions. */
  reload: () => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const PermissionsContext = createContext<PermissionsContextValue>({
  loading:       true,
  permissions:   [],
  hasPermission: () => false,
  reload:        async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

/**
 * Wrap the app in <PermissionsProvider> (inside AuthProvider, next to
 * ModuleProvider) so any component can call usePermissions() without
 * prop-drilling. Reads ONLY user.id from AuthContext.
 */
export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { workspaceId, settled } = useWorkspaceId();
  const [loading, setLoading] = useState(true);
  // Internal Set for O(1) lookups; the public `permissions[]` is derived from it.
  const [permSet, setPermSet] = useState<Set<string>>(new Set());

  /** Load the permission keys for `userId` in the signed-in person's workspace. */
  const loadFor = useCallback(async (userId: string | undefined) => {
    // Logged out — clear to empty (default-deny) and stop loading.
    if (!userId) {
      setPermSet(new Set());
      setLoading(false);
      return;
    }

    // Signed in, but we do not yet know which company. Asking with no workspace would
    // return nothing, which reads as "you may do nothing" — so stay LOADING instead, and
    // let the effect below re-run when the profile lands. Only once it has settled with no
    // workspace is an empty (default-deny) answer the honest one.
    if (!workspaceId) {
      if (settled) {
        setPermSet(new Set());
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      // Query 1 — the role_ids this user holds in the workspace.
      const { data: urData, error: urErr } = await supabase
        .from('user_roles')
        .select('role_id')
        .eq('user_id', userId)
        .eq('workspace_id', workspaceId);

      if (urErr) {
        console.warn('PermissionsContext: user_roles load failed:', urErr);
        setPermSet(new Set());
        return;
      }

      const roleIds = (urData ?? []).map((r: unknown) => (r as { role_id: string }).role_id);
      if (roleIds.length === 0) {
        // No roles assigned — grant nothing.
        setPermSet(new Set());
        return;
      }

      // Query 2 — the permission_keys granted to those roles.
      const { data: rpData, error: rpErr } = await supabase
        .from('role_permissions')
        .select('permission_key')
        .eq('workspace_id', workspaceId)
        .in('role_id', roleIds);

      if (rpErr) {
        console.warn('PermissionsContext: role_permissions load failed:', rpErr);
        setPermSet(new Set());
        return;
      }

      setPermSet(new Set((rpData ?? []).map((r: unknown) => (r as { permission_key: string }).permission_key)));
    } catch (err) {
      // Network error or unexpected throw — default-deny (empty set) and log.
      console.warn('PermissionsContext load failed:', err);
      setPermSet(new Set());
    } finally {
      setLoading(false);
    }
  }, [workspaceId, settled]);

  const reload = useCallback(() => loadFor(user?.id), [loadFor, user?.id]);

  useEffect(() => {
    // Safety valve — never stay stuck on loading more than 5 s (mirrors
    // ModuleContext / AuthContext).
    const timeout = setTimeout(() => setLoading(false), 5000);
    loadFor(user?.id).finally(() => clearTimeout(timeout));
    return () => clearTimeout(timeout);
  }, [user?.id, loadFor]);

  const hasPermission = useCallback(
    (key: string) => permissionGranted(permSet, key),
    [permSet]
  );

  const permissions = useMemo(() => Array.from(permSet), [permSet]);

  return (
    <PermissionsContext.Provider value={{ loading, permissions, hasPermission, reload }}>
      {children}
    </PermissionsContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Access the current user's permissions from any component.
 *
 * @example
 * const { hasPermission, loading } = usePermissions();
 * if (loading) return <Spinner />;
 * if (!hasPermission('finance.invoice.manage')) return null;
 */
export function usePermissions() {
  return useContext(PermissionsContext);
}
